import axios from "axios";
import { BASE_BACKEND_URL, DATA_RETRIEVAL_BASE_URL } from "../constants";
import i18n, { normalizeLanguage } from "../i18n";
import {
  ensureAccessTokenThunk,
  logoutThunk,
} from "../features/user/userThunks";
import { isTokenValid } from "./tokenUtils";

export const checkToken = (access_token) => {
  return isTokenValid(access_token);
};

const getRequestLanguage = () =>
  normalizeLanguage(i18n.resolvedLanguage || i18n.language);

const getInstanceHeaders = () => {
  const id = window.__RUNTIME_CONFIG__?.PUBLIC__INSTANCE_ID;
  return id ? { 'X-Instance-Id': id } : {};
};

export class ApiClient {
  constructor({
    baseURL,
    store = undefined,
    headers = {},
    allowTokenRefresh = false,
  } = {}) {
    this.baseURL = baseURL;
    this.store = store;
    this.headers = headers;
    this.allowTokenRefresh = allowTokenRefresh;

    this.http = axios.create({ baseURL: this.baseURL });

    if (this.store) {
      this.http.interceptors.request.use(
        async (config) => {
          try {
            await this.store
              ?.dispatch?.(
                ensureAccessTokenThunk({
                  allowRefresh:
                    this.allowTokenRefresh && config.skipAuthRefresh !== true,
                  trigger: "http_request",
                })
              )
              ?.unwrap?.();
          } catch (error) {
            await this.store?.dispatch?.(logoutThunk());
            return Promise.reject(error);
          }

          config.headers = {
            ...(config.headers || {}),
            "Accept-Language": getRequestLanguage(),
            ...getInstanceHeaders(),
            authorization: `Bearer ${this.store?.getState?.()?.user?.token_access}`,
            ...this.headers,
          };

          return config;
        },
        (error) => Promise.reject(error)
      );

      this.http.interceptors.response?.use?.(
        (response) => response,
        async (error) => {
          if (error?.response?.status === 403 &&
              error?.response?.data?.code === 'instance_mismatch') {
            await this.store?.dispatch?.(logoutThunk());
            return Promise.reject(error);
          }
          const originalRequest = error?.config;
          const canRetry =
            error?.response?.status === 401 &&
            originalRequest &&
            !originalRequest.__authRetry &&
            originalRequest.skipAuthRefresh !== true;

          if (!canRetry) return Promise.reject(error);

          originalRequest.__authRetry = true;

          try {
            await this.store
              ?.dispatch?.(
                ensureAccessTokenThunk({
                  allowRefresh: this.allowTokenRefresh,
                  forceRefresh: true,
                  trigger: "http_401",
                })
              )
              ?.unwrap?.();

            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              authorization: `Bearer ${
                this.store?.getState?.()?.user?.token_access
              }`,
            };

            return this.http.request(originalRequest);
          } catch (refreshError) {
            await this.store?.dispatch?.(logoutThunk());
            return Promise.reject(refreshError);
          }
        }
      );
    } else {
      this.http.interceptors.request.use(
        (config) => {
          config.headers = {
            ...(config.headers || {}),
            "Accept-Language": getRequestLanguage(),
            ...getInstanceHeaders(),
            ...this.headers,
          };
          return config;
        },
        (error) => Promise.reject(error)
      );
    }
  }

  static public(headers = {}) {
    return new ApiClient({ baseURL: BASE_BACKEND_URL, headers });
  }

  static private(store, headers = {}) {
    return new ApiClient({
      baseURL: BASE_BACKEND_URL,
      store,
      headers,
      allowTokenRefresh: false,
    });
  }

  static dataRetrieval(store, headers = {}) {
    return new ApiClient({
      baseURL: DATA_RETRIEVAL_BASE_URL,
      store,
      headers,
      allowTokenRefresh: false,
    });
  }

  request(config) {
    return this.http.request(config);
  }

  get(url, config) {
    return this.http.get(url, config);
  }

  delete(url, config) {
    return this.http.delete(url, config);
  }

  post(url, data, config) {
    return this.http.post(url, data, config);
  }

  put(url, data, config) {
    return this.http.put(url, data, config);
  }

  patch(url, data, config) {
    return this.http.patch(url, data, config);
  }
}
